import { useState, useEffect } from 'react';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { useDialog, useAuth } from '#root/hooks';
import { isRefundInProcess, getRefundBadgeVariant } from '#root/models/RefundDTO';
import type { RefundDTO } from '#root/models/RefundDTO';
import { GuveryLayout, GuveryPageHeader } from '#root/ui-components';
import { GuveryTableCell, SkeletonRow } from '#root/ui-components/Table';
import { GuveryPaper } from '#root/ui-components/cards';
import { userService } from '#root/services';
import { changeCurrencySymbol } from '#root/utils';
import { Button } from '#root/components/ui';
import WalletBalanceCard from '../UserWallet/WalletBalanceCard';
import RefundDrawer from './RefundDrawer';
import RefundStatusBadge from './RefundStatusBadge';
import styles from './userWalletRefunds.module.scss';
import { WalletInfoPanel } from '../UserWallet/WalletInfoPanel';
import { USER_WALLET } from '#root/constants/navigation';

// --- Types ---

interface RefundOptions {
  banks?: string[];
  currencies?: string[];
  processing_time?: {
    min_days: number;
    max_days: number;
  };
}

// --- Helpers ---

const getRowBackgroundColor = (status: string): string | undefined => {
  const variant = getRefundBadgeVariant(status);
  switch (variant) {
    case 'info':
      return 'rgba(33, 150, 243, 0.08)';
    case 'warning':
      return 'rgba(255, 152, 0, 0.08)';
    case 'success':
      return 'rgba(0, 171, 85, 0.12)';
    default:
      return undefined;
  }
};

// --- Sub-components ---

const RefundRow = ({ row }: { row: RefundDTO }) => {
  const rowStyle = { backgroundColor: getRowBackgroundColor(row.current_status) };

  return (
    <TableRow hover style={rowStyle}>
      <TableCell>
        <div className={styles.cellStack}>
          <div className={styles.idText}>{'#' + row.id}</div>
          <div className={styles.dateText}>
            {format(new Date(row.created_at.replace(/ /g, 'T')), "dd 'de' MMMM 'del' yyyy", {
              locale: es,
            })}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className={styles.cellStack}>
          <div className={styles.bankName}>{row.bank + ' - ' + row.currency}</div>
          <div className={styles.accountDetail}>
            {row.bank_account_number + ' : ' + row.bank_account_holder}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className={styles.amountStack}>
          <div className={styles.amountText}>
            {changeCurrencySymbol('DOLARES')} {row.amount}
          </div>
          <RefundStatusBadge
            status={row.current_status}
            paid_at={row.paid_at}
            status_label={row.status_label}
          />
        </div>
      </TableCell>
    </TableRow>
  );
};

// --- Main Component ---

function UserWalletRefunds() {
  const refundDialog = useDialog();
  const { usuario } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refunds, setRefunds] = useState<RefundDTO[]>([]);
  const [refundOptions, setRefundOptions] = useState<RefundOptions>({});

  const fetchData = async () => {
    setLoading(true);
    const res = await userService.refunds();
    if (res.error) return setLoading(false);
    const data = res.data;
    setRefunds(data.refunds);
    setRefundOptions(data.refund_options);
    setLoading(false);
  };

  const hasRefundInProcess = refunds.some(
    (refund) => refund.is_pending ?? isRefundInProcess(refund.current_status),
  );

  useEffect(() => {
    fetchData();
  }, [refundDialog.open]);

  return (
    <GuveryLayout className="user-wallet">
      <GuveryPageHeader
        backButton
        title="Mis Reembolsos"
        extra={[
          <Button key="1" variant="outline" size="sm" to={USER_WALLET}>
            Movimientos
          </Button>,
          <Button
            key="2"
            disabled={usuario.saldo_est !== 1 || parseFloat(usuario.saldo ?? '0') < 1}
            variant="outline"
            onClick={refundDialog.openDialog}
            size="sm">
            Solicitar Reembolso
          </Button>,
        ]}
      />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <WalletBalanceCard
            saldo={usuario.saldo}
            saldoEstado={usuario.saldo_est}
            saldoEvento={usuario.saldo_evento}
          />
        </Grid>
        <Grid item xs={12}>
          <WalletInfoPanel
            subtitle="Mis Reembolsos"
            processingTime={
              refundOptions?.processing_time
                ? `${refundOptions.processing_time.min_days}-${refundOptions.processing_time.max_days} días útiles`
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12}>
          <GuveryPaper
            disableHover
            disableElevation
            className="card-movements">
            <div className="header">
              <h4>Reembolsos</h4>
            </div>
            <Table style={{ minWidth: 650 }} aria-label="refunds table">
              <TableHead>
                <TableRow>
                  <GuveryTableCell>#ID / Fecha</GuveryTableCell>
                  <GuveryTableCell>Banco / Cuenta - Titular</GuveryTableCell>
                  <GuveryTableCell>Importe</GuveryTableCell>
                </TableRow>
              </TableHead>
              {!loading ? (
                <TableBody>
                  {refunds.map((row, index) => (
                    <RefundRow row={row} key={index} />
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  {Array.from(new Array(10)).map((_data, index) => (
                    <SkeletonRow columnsCount={3} key={index} />
                  ))}
                </TableBody>
              )}
            </Table>
          </GuveryPaper>
        </Grid>
      </Grid>
      <RefundDrawer
        open={refundDialog.open}
        onClose={refundDialog.closeDialog}
        refundInProcess={refunds.length > 0 && hasRefundInProcess}
        refundOptions={refundOptions}
        amount={parseFloat(usuario.saldo ?? '0')}
      />
    </GuveryLayout>
  );
}

export default UserWalletRefunds;
